import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';

import { AIActivityTypes } from 'models/ai_activity/ai_activity_item';
import analytics from 'scripts/lib/analytics';
import BubbleContent from 'components/customer/conversation_history/conversation_items_v2/content/bubble_content';
import ConversationItem from 'models/conversation_item';
import ConversationSummaryItemFooter from 'components/customer/conversation_history/conversation_items_v2/ai_activity/conversation_summary_item_footer';
import ItemContent from 'components/customer/conversation_history/conversation_items_v2/item_content';
import ItemMetadata from 'components/customer/conversation_history/conversation_items_v2/item_metadata';
import RedactedItem from 'components/customer/conversation_history/conversation_items_v2/redacted_item';
import SummaryIcon from 'components/common/icons/stroke/summary-stroke';

export const FEEDBACK_NEGATIVE = 'negative';
export const FEEDBACK_POSITIVE = 'positive';

export const FEEDBACK_VISIBLE_SECONDS = 3 * 60; // visibility interval in seconds

export default function ConversationSummaryItem({ className, item }) {
  const activityType = item?.content?.activityType || '';
  if (!item?.content || activityType !== AIActivityTypes.CONVERSATION_SUMMARY) return null;

  return (
    <ItemContent className="aiConversationSummaryItem" isAuditItem={false} item={item}>
      <div>
        <SummaryMetaIcon />
        <ItemMetadata item={item} showMetadata text="created a conversation summary" timestamp={item.timestamp} />
      </div>
      {renderConversationSummary(item)}
    </ItemContent>
  );

  function renderConversationSummary(conversationItem) {
    const classes = classnames('conversationSummaryContent', className);
    const redacted = _.get(conversationItem, 'content.isRedacted');
    const renderedContent = redacted ? <RedactedItem /> : renderSummaryContent(conversationItem);

    return (
      <StyledBubbleContent className={classes} item={conversationItem}>
        <div className="content-wrapper">{renderedContent}</div>
      </StyledBubbleContent>
    );
  }

  function renderSummaryContent(conversationItem) {
    const content = _.get(conversationItem, 'content');
    const meta = _.get(content, 'meta') || {};

    return (
      <>
        {renderCaptionSection(meta)}
        {renderDetailsSection(meta)}
        <ConversationSummaryItemFooter
          feedbackVisibilityInterval={FEEDBACK_VISIBLE_SECONDS}
          item={conversationItem}
          onThumbsDown={onThumbsDown}
          onThumbsUp={onThumbsUp}
        />
      </>
    );
  }

  function renderCaptionSection(itemMeta) {
    const caption = _.trim(itemMeta.caption);
    return caption ? (
      <>
        <SectionTitle>Reason for customer contact</SectionTitle>
        {caption}
      </>
    ) : null;
  }

  function renderDetailsSection(itemMeta) {
    const details = _.trim(itemMeta.details);
    if (!details) return null;

    const parts = details
      .split('\n')
      .map(part => _.trim(part))
      .filter(part => !!part);
    if (!parts.length) return null;

    const renderedParts = parts.map((part, idx) => <li key={idx}>{part}</li>);
    return (
      <>
        <SectionTitle>Summary of the conversation</SectionTitle>
        <StyledDetailsList>{renderedParts}</StyledDetailsList>
      </>
    );
  }

  function onThumbsUp() {
    trackFeedbackEvent({ feedback: FEEDBACK_POSITIVE });
  }
  function onThumbsDown() {
    trackFeedbackEvent({ feedback: FEEDBACK_NEGATIVE });
  }

  function trackFeedbackEvent(feedbackEvent = {}) {
    analytics.track('Timeline Conversation Summary Feedback Provided', {
      customerId: item.customerId,
      conversationId: item.conversationId,
      conversationItemId: item.id,
      summaryType: 'complete',
      ...feedbackEvent,
    });
  }
}

//
// Styled components
//
const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: ${p => p.theme.fontWeight.mediumHeavy};
  margin-top: 4px;
`;

const StyledBubbleContent = styled(BubbleContent)`
  border: 1px solid mediumpurple;
`;

const StyledDetailsList = styled.ul`
  &&& {
    margin-top: 2px;
  }
`;

const SummaryMetaIcon = styled(SummaryIcon)`
  fill: ${p => p.theme.colors.gray600};
  height: 10px;
  margin-right: 4px;
  margin-top: -1px;
  vertical-align: middle;
  width: auto;
`;

ConversationSummaryItem.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};
